import React from 'react'
import { Popover, Stack } from '@mui/material'
import { Button, useAnimatedHeight } from '@repo/ui'
import { PlusIcon } from 'lucide-react'
import { SortField } from 'components/UI/SortField'

export type AdditionalSort = { field: string; direction: 'ASC' | 'DESC' }

export type SortConfigPopoverProps = {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  primaryField: string
  primaryDirection: 'ASC' | 'DESC'
  additionalSorts: AdditionalSort[]
  disabledFields: string[]
  onPrimaryFieldChange: (field: string) => void
  onPrimaryDirectionChange: (dir: 'ASC' | 'DESC') => void
  onClearAll: () => void
  onChangeAdditional: (index: number, next: AdditionalSort) => void
  onRemoveAdditional: (index: number) => void
  onAddAdditional: (nextField: string) => void
  /**
   * The sortable field keys, when they are not the certificates list's.
   *
   * Optional and defaulted for the same reason as `SortMenuDesktop.fields`: the
   * certificate sub-views share this popover's PRESENTATION while sorting on their
   * own columns. It was hardcoded in three places in this file (both `SortField`
   * lists and the "Add another sort" picker) — one prop, threaded to all three, so
   * they cannot disagree.
   *
   * Labels and direction wording are NOT passed: `SortField` resolves a key to its
   * display name and `SORT_FIELD_TYPES` resolves it to "Newest first" vs "A - Z",
   * so a field declared once is labelled correctly everywhere.
   */
  availableFields?: string[]
}

const CERTIFICATE_FIELDS = ['name', 'status', 'last_activity']

export const SortConfigPopover: React.FC<SortConfigPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  primaryField,
  primaryDirection,
  additionalSorts,
  disabledFields,
  onPrimaryFieldChange,
  onPrimaryDirectionChange,
  onClearAll,
  onChangeAdditional,
  onRemoveAdditional,
  onAddAdditional,
  availableFields = CERTIFICATE_FIELDS,
}) => {
  /*
   * VERA-315 — "Add another sort" grows this popover by a whole row, and clearing one
   * shrinks it again. Both used to be an instant jump. The shared `@repo/ui`
   * `useAnimatedHeight` (the platform `Modal`'s own mechanism) measures the change and
   * interpolates it, and no-ops under `prefers-reduced-motion`. See the longer note in
   * `DrawerPopup` for why the reduced-motion gate has to live in JS.
   *
   * The growth direction is already right for this: `anchorOrigin` bottom +
   * `transformOrigin` top pins the popover's TOP edge to the trigger, so the animated
   * height extends downwards and the rows the user is reading do not move under them.
   */
  const animatedPaperRef = useAnimatedHeight<HTMLDivElement>()

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      /*
       * RIGHT-ALIGNED to the trigger, so the popover grows LEFTWARD.
       *
       * Two things were wrong with `horizontal: 'left'`, and the second is the
       * one that gives the rule:
       *
       * 1. It overflowed. The trigger sits in a toolbar hard against the content
       *    column's right edge, so a 360px-minimum panel pinned by its LEFT edge
       *    and grown rightward runs off the viewport — the sort-direction select
       *    was clipped mid-control.
       *
       * 2. It disagreed with the step before it. `SortMenuDesktop` — the field
       *    list this panel replaces, opened from the SAME `sortButtonRef` — has
       *    always been `horizontal: 'right'`. So choosing a field made the
       *    surface jump sideways between two steps of one flow, which reads as a
       *    new panel appearing somewhere else rather than the same panel
       *    advancing. Matching the origins is what makes the two steps read as
       *    one surface, and it fixes the overflow as a side effect.
       *
       * The vertical pair is unchanged and deliberately so: `bottom`/`top` pins
       * the panel's TOP edge to the trigger, which is what lets "Add another
       * sort" grow the panel downwards without moving the rows already being
       * read (see `useAnimatedHeight` above).
       */
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ ref: animatedPaperRef, sx: { p: 2, borderRadius: '16px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 360 } }}
    >
      <Stack spacing={0} sx={{ gap: 1.5, '& > * + *': { marginTop: '0 !important' }, '& .MuiFormControl-root': { marginTop: '0 !important' }, '& .MuiInputBase-root': { marginTop: '0 !important' } }}>
        <SortField
          field={primaryField}
          direction={primaryDirection as any}
          inline
          showClear={true}
          availableFields={availableFields}
          disabledFields={disabledFields}
          onFieldChange={onPrimaryFieldChange}
          onDirectionChange={onPrimaryDirectionChange as any}
          onClear={onClearAll}
        />
        {additionalSorts.map((sort, index) => (
          <SortField
            key={index}
            field={sort.field}
            direction={sort.direction as any}
            inline
            showClear={true}
            availableFields={availableFields}
            disabledFields={disabledFields}
            onFieldChange={(field) => onChangeAdditional(index, { field, direction: sort.direction })}
            onDirectionChange={(direction) => onChangeAdditional(index, { field: sort.field, direction: direction as any })}
            onClear={() => onRemoveAdditional(index)}
          />
        ))}
        {(() => {
          const total = additionalSorts.length + (primaryField ? 1 : 0)
          // `availableFields.length`, not a literal 3. The 3 WAS the certificate
          // field count, so this is byte-identical for the list — but a caller with
          // two sortable fields (Activity) would otherwise be offered an "Add
          // another sort" that has nothing left to add and falls back to a field
          // already in use.
          if (total >= availableFields.length) return null
          return (
            /*
              A TEXT button, not a filled secondary.

              As a grey `secondary` it carried the same weight as `Clear` directly
              beneath it and read as one of two equal choices — but it is not a
              decision about this menu, it is a way to EXTEND the list above it.
              A filled control also competes with the sort rows it sits under,
              which are the actual content here.

              Platform's equivalent is likewise unfilled — its Reset in the Layout
              menu is an underlined link, not a button, for the same reason: an
              affordance that adds or restores state should be quieter than the
              state itself.
            */
            <Button
              variant="text"
              fullWidth
              onClick={() => {
                const used = new Set(disabledFields)
                const next = availableFields.find(f => !used.has(f)) || availableFields[0]
                onAddAdditional(next)
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              <PlusIcon size={16} style={{ marginRight: 8 }} />
              Add another sort
            </Button>
          )
        })()}
        {/*
          "Reset", not "Clear" — parity with the Layout popover sitting two
          controls away in the same toolbar. Both are popovers whose bottom
          control restores the surface to nothing-applied, and two words for one
          concept in one row is the drift this pass exists to remove.

          Platform's sort menu says "Clear", but its Layout menu says "Reset", so
          platform is not internally consistent here either and copying it would
          just import the inconsistency. The shared `FilterSheetFooter` already
          defaults to `Reset` and documents `Clear` as the exception for a footer
          with no Apply to pair against — this popover has no Apply either, but it
          sits beside a Reset, and the neighbour wins.
        */}
        {/*
          NOT `destructive`. Reset restores the surface to nothing-applied; it
          destroys no record and is undone by re-applying the sort, so it fails
          §38's test ("does a row disappear from the database?") and sits outside
          §32's destructive tier, which is for actions that DESTROY.

          Red here is not merely unearned, it is corrosive: spending the danger
          colour on the most reversible control in the toolbar teaches the reader
          that red means "this clears something", which is precisely the reading
          that makes red fail to stop them at a real delete.
        */}
        <Button variant="secondary" fullWidth onClick={onClearAll}>Reset</Button>
      </Stack>
    </Popover>
  )
}

export default SortConfigPopover


